import AxiosConfig from '../utils/AxiosConfig';

class B2BOrderService {
  // Obtener todos los pedidos B2B
  async getOrders(page = 1, pageSize = 10, searchTerm = '', options = {}) {
    try {
      const {
        sortField,
        sortDirection,
        filterField,
        filterOperator,
        filterValue,
        destinationLocationId
      } = options;

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });
      if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (sortField) params.append('sortField', sortField);
      if (sortDirection) params.append('sortDirection', sortDirection);
      if (filterField) params.append('filterField', filterField);
      if (filterOperator) params.append('filterOperator', filterOperator);
      if (filterValue !== undefined && filterValue !== null && String(filterValue).trim() !== '') {
        params.append('filterValue', String(filterValue).trim());
      }
      if (destinationLocationId) {
        params.append('destinationLocationId', String(destinationLocationId));
      }
      const response = await AxiosConfig.get(`/b2b/orders?${params}`);
      // El backend devuelve {error: false, status_code: 200, data: {orders: [...], totalCount: 17, currentPage: 1, totalPages: 2, pageSize: 10}}
      const data = response.data.data;
      return {
        orders: data.orders || [],
        totalCount: data.totalCount || 0,
        totalPages: data.totalPages || 1,
        currentPage: data.currentPage || page,
        pageSize: data.pageSize || pageSize
      };
    } catch (error) {
      console.error('Error fetching B2B orders:', error);
      throw error;
    }
  }

  // Obtener un pedido específico por ID
  async getOrderById(orderId) {
    try {
      const response = await AxiosConfig.get(`/b2b/orders/${orderId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching B2B order:', error);
      throw error;
    }
  }

  // Crear un nuevo pedido B2B
  async createOrder(orderData) {
    try {
      const response = await AxiosConfig.post('/b2b/orders', orderData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating B2B order:', error);
      throw error;
    }
  }

  // Actualizar un pedido B2B
  async updateOrder(orderId, orderData) {
    try {
      const response = await AxiosConfig.put(`/b2b/orders/${orderId}`, orderData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating B2B order:', error);
      throw error;
    }
  }

  // Actualizar estado de un pedido B2B
  async updateOrderStatus(orderId, statusData) {
    try {
      const response = await AxiosConfig.put(`/b2b/orders/${orderId}/status`, statusData);
      return response.data.data.order;
    } catch (error) {
      console.error('Error updating B2B order:', error);
      throw error;
    }
  }

  // Eliminar un pedido B2B
  async deleteOrder(orderId) {
    try {
      const response = await AxiosConfig.delete(`/b2b/orders/${orderId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error deleting B2B order:', error);
      throw error;
    }
  }

  // Obtener pedidos de un cliente específico
  async getOrdersByClient(clientId, page = 1, pageSize = 10, searchTerm = '', options = {}) {
    try {
      const {
        sortField,
        sortDirection,
        filterField,
        filterOperator,
        filterValue,
        destinationLocationId
      } = options;

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString()
      });
      if (searchTerm && typeof searchTerm === 'string' && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (sortField) params.append('sortField', sortField);
      if (sortDirection) params.append('sortDirection', sortDirection);
      if (filterField) params.append('filterField', filterField);
      if (filterOperator) params.append('filterOperator', filterOperator);
      if (filterValue !== undefined && filterValue !== null && String(filterValue).trim() !== '') {
        params.append('filterValue', String(filterValue).trim());
      }
      if (destinationLocationId) {
        params.append('destinationLocationId', String(destinationLocationId));
      }
      params.append('clientId', String(clientId));
      const response = await AxiosConfig.get(`/b2b/orders?${params}`);
      const data = response.data.data;

      return {
        orders: data.orders || [],
        totalCount: data.totalCount || 0,
        totalPages: data.totalPages || 1,
        currentPage: data.currentPage || page
      };
    } catch (error) {
      throw error;
    }
  }

  // Obtener pagos de un pedido
  async getOrderPayments(orderId) {
    try {
      const response = await AxiosConfig.get(`/b2b/orders/${orderId}/payments`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching order payments:', error);
      throw error;
    }
  }

  async getAvailableBranches() {
    try {
      const response = await AxiosConfig.get('/b2b/orders/branches');
      return response.data.data?.branches || [];
    } catch (error) {
      console.error('Error fetching B2B branches:', error);
      throw error;
    }
  }

  async getFranchiseClients(destinationLocationId) {
    try {
      const params = new URLSearchParams({
        destinationLocationId: String(destinationLocationId)
      });
      const response = await AxiosConfig.get(`/b2b/orders/franchise-clients?${params}`);
      return response.data.data?.clients || [];
    } catch (error) {
      console.error('Error fetching franchise B2B clients:', error);
      throw error;
    }
  }

  // Crear un nuevo pago para un pedido
  async createPayment(orderId, paymentData) {
    try {
      const response = await AxiosConfig.post(`/b2b/orders/${orderId}/payments`, paymentData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating payment:', error);
      throw error;
    }
  }

  // Actualizar estado de pago de un pedido
  async updatePaymentStatus(orderId, paymentStatusData) {
    try {
      const response = await AxiosConfig.put(`/b2b/orders/${orderId}/payment-status`, paymentStatusData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  }

  // Generar PDF de factura del pedido validando acceso B2B por pedido
  async generateOrderPDF(orderId) {
    try {
      const response = await AxiosConfig.get(`/b2b/orders/${orderId}/invoice-pdf`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Error generating order PDF:', error);
      // Con responseType 'blob' el cuerpo de un error también llega como Blob
      // (no como el JSON { error_message } ya parseado), así que hay que leerlo
      // a mano para no perder el motivo real (factura no emitida, etc.).
      const backendMessage = await this._parseBlobErrorMessage(error);
      if (backendMessage) {
        const enrichedError = new Error(backendMessage);
        enrichedError.isInvoiceNotAvailable = true;
        throw enrichedError;
      }
      throw error;
    }
  }

  async _parseBlobErrorMessage(error) {
    const data = error?.response?.data;
    if (!(data instanceof Blob) || data.type.indexOf('application/json') === -1) {
      return null;
    }
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error_message || null;
    } catch (_parseError) {
      return null;
    }
  }

  // Reintentar manualmente la emisión de factura electrónica de un pedido B2B
  // que quedó sin facturar (invoiceStatus PENDING o FAILED).
  async retryInvoiceEmission(orderId) {
    try {
      const response = await AxiosConfig.post(
        `/b2b/orders/${orderId}/retry-invoice`,
        {},
        { skipSnackbar: true }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error retrying invoice emission:', error);
      throw error;
    }
  }
}

export default new B2BOrderService();
